import {
  ChangeDetectionStrategy,
  Component,
  input,
  viewChild,
  AfterViewInit,
  computed,
  Signal,
  effect,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, startWith, tap } from 'rxjs';

import { Nullish } from '@plugins/shared/types';

import { ProfileFieldPickerComponent } from '../profile-fields-picker/profile-field-picker.component';

@Component({
  selector: 'plugins-user-profile-field-picker-toggler',
  standalone: false,
  templateUrl: './user-profile-field-picker-toggler.component.html',
  styleUrl: './user-profile-field-picker-toggler.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileFieldPickerTogglerComponent implements AfterViewInit {
  private readonly injector = inject(Injector);
  readonly profileFieldPicker = viewChild.required(ProfileFieldPickerComponent);

  /** Form control for the selected profile fields.  */
  readonly profileFieldsControl =
    input.required<FormControl<string[] | Nullish>>();

  /** List of profile field keys that are allowed to be selected. Passing `null` allows all. */
  readonly profileFieldsAllowed = input<string[] | Nullish>();

  /** List of profile field keys that are required (checked and disabled/cannot be unchecked). */
  readonly profileFieldsRequired = input<string[]>([]);

  /** Color that applies to the checkboxes. */
  readonly settingsColor = input<string>();

  readonly description = input<string>();

  readonly selectAllControl = new FormControl(false, { nonNullable: true });

  isIndeterminateState: Signal<boolean> | undefined;

  constructor() {
    effect(() => {
      const control = this.profileFieldsControl();

      const sub = control.statusChanges
        .pipe(
          startWith(control.status),
          tap((status) => {
            if (status === 'DISABLED') {
              this.selectAllControl.disable({ emitEvent: false });
            } else {
              this.selectAllControl.enable({ emitEvent: false });
            }
          }),
        )
        .subscribe();

      return () => {
        sub.unsubscribe();
      };
    });
  }

  ngAfterViewInit(): void {
    const picker = this.profileFieldPicker();

    runInInjectionContext(this.injector, () => {
      const selectedFields = toSignal(picker.control.valueChanges, {
        initialValue: picker.control.getRawValue(),
      });

      this.isIndeterminateState = computed(() => {
        const allSelected = picker.allSelected();
        if (allSelected) {
          return false;
        }

        const fields = selectedFields();
        const requiredFields = this.profileFieldsRequired();
        return !!fields?.some(
          (field: string) => !requiredFields.includes(field),
        );
      });

      const selectAllValueChange = toSignal(
        this.selectAllControl.valueChanges.pipe(distinctUntilChanged()),
      );

      effect(() => {
        const allSelected = selectAllValueChange();
        if (allSelected === undefined) {
          return;
        }
        picker.selectAll(allSelected);
      });

      effect(() => {
        const allSelected = picker.allSelected();
        this.selectAllControl.setValue(allSelected, { emitEvent: false });
      });
    });
  }
}
